import { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DevelopmentDetailsViewModel from '../../ViewModels/DevelopmentDetails/DevelopmentDetailsViewModel';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { APIContext } from '../../../Contexts/APIContext';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import OpeningTimes from './OpeningTimes';
import Overview from './Overview';
import Comments from './Comments';
import ResidentialUnits from './ResidentialUnits';
import Employees from './Employees';
import Parking from './Parking';
import Facilities from './Facilities';
import RetailDetails from './RetailDetails';
import ResidentialTable from './ResidentialTable';
import Grid from '@mui/material/Grid2';
import NearestSimilarSite from './NearestSimilarSite';
import EditControl from '../EditControl';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

interface IProps {
    hasParkingDetails?: boolean | null;
    reloadSite?: () => void;
}

export default function DevelopmentUnits({ hasParkingDetails, reloadSite }: IProps) {
    const { get, post, showMessage } = useContext(APIContext);
    const [developmentDetailsList, setDevelopmentDetailsList] = useState<DevelopmentDetailsViewModel[]>([]);
    const [selectedTab, setSelectedTab] = useState(0);
    const [components, setComponents] = useState<JSX.Element[]>([]);
    const [readOnly, setReadOnly] = useState(true);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newDevelopmentName, setNewDevelopmentName] = useState('');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [developmentToDeleteIndex, setDevelopmentToDeleteIndex] = useState<number | null>(null);

    const location = useLocation();
    const params = new URLSearchParams(location.search);

    const siteRef = params.get('siteRef') || '';

    const formContext = useForm<DevelopmentDetailsViewModel>({
        defaultValues: new DevelopmentDetailsViewModel(),
    });

    const { reset } = formContext;

    useEffect(() => {
        if (!siteRef) {
            return;
        }

        getDevelopmentDetailsForSite();
    }, [siteRef]);

    useEffect(() => {
        const detail = developmentDetailsList[selectedTab];

        if (!detail) {
            setComponents([]);
            return;
        }

        const landuse = siteRef.split('-')[1];
        const subUse = siteRef.split('-')[2];

        const componentsList: JSX.Element[] = [
            <Overview key="overview" isReadOnly={readOnly} landuse={landuse} subuse={subUse} />,
        ];

        if (detail.openingTimes || !readOnly) {
            componentsList.push(
                <OpeningTimes key="openingTimes" isReadOnly={readOnly} landuse={landuse} subuse={subUse} />,
            );
        }

        if (detail.residentialUnits || !readOnly) {
            componentsList.push(
                <ResidentialUnits key="resUnits" isReadOnly={readOnly} landuse={landuse} subuse={subUse} />,
            );
            componentsList.push(
                <ResidentialTable
                    key="resTable"
                    residentialUnits={detail.residentialUnits}
                    landuse={landuse}
                    subuse={subUse}
                    readOnly={readOnly}
                />,
            );
        }

        if (detail.comments || !readOnly) {
            componentsList.push(<Comments key="comments" isReadOnly={readOnly} />);
        }

        componentsList.push(<NearestSimilarSite key="nearestSimilar" isReadOnly={readOnly} />);

        if (detail.employees || !readOnly) {
            componentsList.push(
                <Employees key="employees" isReadOnly={readOnly} landuse={landuse} subuse={subUse} />,
            );
        }

        if (detail.facilities || !readOnly) {
            componentsList.push(
                <Facilities key="facilities" isReadOnly={readOnly} landuse={landuse} subuse={subUse} />,
            );
        }

        if (detail.parkingDetails || !readOnly) {
            componentsList.push(
                <Parking key="parking" isReadOnly={readOnly} hasParkingDetails={hasParkingDetails} />,
            );
        }

        if (detail.retailDetails || !readOnly) {
            componentsList.push(
                <RetailDetails key="retail" isReadOnly={readOnly} landuse={landuse} subuse={subUse} />,
            );
        }

        setComponents(componentsList);
    }, [developmentDetailsList, selectedTab, readOnly, siteRef, hasParkingDetails]);

    const getDevelopmentDetailsForSite = (isEdit?: boolean) => {
        get(
            'DevelopmentDetails',
            `GetDevelopmentDetailsForSite?reference=${siteRef}&siteHasFullParkingDetails=${hasParkingDetails}&isEdit=${isEdit ?? false}`,
            (data: DevelopmentDetailsViewModel[]) => {
                if (!Array.isArray(data) || data.length === 0) {
                    return;
                }

                setDevelopmentDetailsList(data);
                reset({ ...data[0] });
            },
        );
    };

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        const detail = developmentDetailsList[newValue];

        if (!detail) {
            return;
        }

        setSelectedTab(newValue);
        reset({ ...detail });
    };

    const submit = (form: any) => {
        post('DevelopmentDetails', `UpdateDevelopmentDetails?reference=${siteRef}`, form, async () => {
            await getDevelopmentDetailsForSite(false);
            showMessage('success', 'Development units updated successfully');
            setReadOnly(true);
            getDevelopmentDetailsForSite();
        });
    };

    const openAddDevelopmentDialog = () => {
        setNewDevelopmentName('');
        setIsAddDialogOpen(true);
    };

    const closeAddDevelopmentDialog = () => {
        setIsAddDialogOpen(false);
    };

    const handleAddDevelopment = async () => {
        const trimmedName = newDevelopmentName.trim();

        if (!trimmedName) {
            showMessage('error', 'Please enter a development name');
            return;
        }

        await get('DevelopmentDetails', `AddDevelopment?reference=${siteRef}&tradeName=${encodeURIComponent(trimmedName)}`, () => {
            getDevelopmentDetailsForSite(true);
            reloadSite?.();
            showMessage('success', 'Development added successfully');
        });

        setIsAddDialogOpen(false);
    };

    const openDeleteDevelopmentDialog = (event: React.MouseEvent, index: number) => {
        event.stopPropagation();

        if (developmentDetailsList.length <= 1) {
            showMessage('error', 'At least one development is required');
            return;
        }

        setDevelopmentToDeleteIndex(index);
        setIsDeleteDialogOpen(true);
    };

    const closeDeleteDevelopmentDialog = () => {
        setIsDeleteDialogOpen(false);
        setDevelopmentToDeleteIndex(null);
    };

    const handleDeleteDevelopment = async () => {
        if (developmentToDeleteIndex === null) {
            return;
        }

        const detail = developmentDetailsList[developmentToDeleteIndex];

        if (!detail) {
            closeDeleteDevelopmentDialog();
            return;
        }

        // Assuming a matching API endpoint similar to AddDevelopment.
        await get(
            'DevelopmentDetails',
            `DeleteDevelopment?reference=${siteRef}&devNum=${detail.devNo}`,
            () => {
                showMessage('success', 'Development removed successfully');
                setSelectedTab((previous) => (previous > 0 ? previous - 1 : 0));
                getDevelopmentDetailsForSite(true);
            },
        );

        closeDeleteDevelopmentDialog();
    };

    const getDevelopmentNameForDeleteDialog = () => {
        if (developmentToDeleteIndex === null) {
            return '';
        }

        const detail = developmentDetailsList[developmentToDeleteIndex];

        if (!detail) {
            return '';
        }

        return detail.tradeName;
    };

    return (
        <FormContainer formContext={formContext} onSuccess={submit}>
            <Tabs value={selectedTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                {developmentDetailsList.map((detail, index) => (
                    <Tab
                        key={index}
                        sx={{ color: 'black' }}
                        label={
                            <Box display="flex" alignItems="center">
                                <span>{detail.tradeName}</span>
                                {!readOnly && developmentDetailsList.length > 1 && (
                                    <IconButton
                                        size="small"
                                        onClick={(event) => openDeleteDevelopmentDialog(event, index)}
                                        sx={{ ml: 0.5 }}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        }
                    />
                ))}
                {!readOnly && (
                    <Button onClick={openAddDevelopmentDialog} startIcon={<AddIcon />}>
                        Add
                    </Button>
                )}
            </Tabs>

            {developmentDetailsList.length === 0 && (
                <Typography sx={{ p: 2 }}>
                    No development units are available. Enter edit mode, then select Add to create the first development.
                </Typography>
            )}

            <Grid container justifyContent="stretch">
                {components.map((component, index) => (
                    <Grid size={4} key={index}>
                        {component}
                    </Grid>
                ))}
            </Grid>
            <EditControl readOnly={readOnly} setReadOnly={setReadOnly} />



            <Dialog open={isAddDialogOpen} onClose={closeAddDevelopmentDialog} fullWidth maxWidth="sm">
                <DialogTitle>Add development</DialogTitle>
                <DialogContent>
                    <TextField
                        margin="normal"
                        fullWidth
                        label="Development name"
                        value={newDevelopmentName}
                        onChange={(e) => setNewDevelopmentName(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeAddDevelopmentDialog}>
                        Cancel
                    </Button>
                    <Button onClick={handleAddDevelopment} variant="contained">
                        Add
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={isDeleteDialogOpen} onClose={closeDeleteDevelopmentDialog} fullWidth maxWidth="xs">
                <DialogTitle>Remove development</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove &quot;{getDevelopmentNameForDeleteDialog()}&quot;?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDevelopmentDialog}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteDevelopment} color="error" variant="contained">
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </FormContainer>
    );
}
